import React from "react";
import Input from "../../Atomos/Inputs/Input";
import CalendarInput from "../../Atomos/Inputs/CalendarInput";
import DropDownMultiple, {
  DropDownOptionBase,
} from "../../staff/DropDownMultiple";
import Excel from "../../Atomos/Icons/Excel";
import Close from "../../Atomos/Icons/Close";
import LegacyButton from "../../Atomos/Buttons/LegacyButton";
import Button from "../../staff/Button";
import ColumnVisibilityMenu from "./ColumnVisibilityMenu";
import FilterOptionsGroup from "./FilterOptionsGroup";
import {
  IFilterData,
  IHeader,
  ISelectedFilterGroup,
  type CustomFilterApplyPayload,
  type FiltersPanelLayout,
} from "./FilteredTable.types";
import { getFilterPanelColClass, isFilterHiddenFromUi } from "./FilteredTable.utils";
import InputText from "../../staff/InputText";
import { useStaffMode } from "../../../hooks/StaffModeProvider";

function selectedFilterGroupLabel (name: string): string {
  const base = name.replace(/:+\s*$/u, "").trim();
  return `${base}:`;
}

function chunkPanelFilters (
  filters: IFilterData[],
): Array<{ group?: string; items: IFilterData[] }> {
  const chunks: Array<{ group?: string; items: IFilterData[] }> = [];
  for (const filter of filters) {
    if (isFilterHiddenFromUi(filter)) {
      continue;
    }
    const group = filter.panelGroup?.trim() || undefined;
    const last = chunks[chunks.length - 1];
    if (group && last?.group === group) {
      last.items.push(filter);
    } else {
      chunks.push({ group, items: [filter] });
    }
  }
  return chunks;
}

interface FiltersPanelProps {
  bannerContent?: React.ReactNode;
  showSelectedFilters?: boolean;
  inlineToolbar?: boolean;
  filtersData: IFilterData[];
  filterValues: Record<string, any>;
  onHandleFilters: (
    field: string,
    value: any,
    fieldName?: string,
    comparison?: string,
    getPresetRange?: (optionId: string) => string,
  ) => void;
  hasSelectedFilters: boolean;
  selectedFilterGroups: ISelectedFilterGroup[];
  onClearAllSelectedFilters: () => void;
  onRemoveSelectedValue: (
    fieldName: string,
    valueToRemove: DropDownOptionBase,
  ) => void;
  showSearchInput: boolean;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  searchToolbarAccessory?: React.ReactNode;
  showExcelExport: boolean;
  onOpenExcelModal: () => void;
  showColumnVisibility: boolean;
  columnsDef: IHeader[];
  columnVisibility: Record<string, boolean>;
  onToggleColumnVisibility: (columnId: string) => void;
  onClearAllColumnVisibility: () => void;
  onSelectAllColumnVisibility: () => void;
  onCustomFilterApply?: (
    panelName: string,
    apiFieldName: string | undefined,
    nextUiState: unknown,
    clause: CustomFilterApplyPayload,
  ) => void;
  containerclassName?: string;
  layout?: FiltersPanelLayout;
  /** Optional content rendered below the search/export row (default layout only). */
  belowSearchContent?: React.ReactNode;
}

export default function FiltersPanel ({
  bannerContent,
  showSelectedFilters = true,
  inlineToolbar = false,
  filtersData,
  filterValues,
  onHandleFilters,
  hasSelectedFilters,
  selectedFilterGroups,
  onClearAllSelectedFilters,
  onRemoveSelectedValue,
  showSearchInput,
  onSearchChange,
  searchPlaceholder,
  searchToolbarAccessory,
  showExcelExport,
  onOpenExcelModal,
  showColumnVisibility,
  columnsDef,
  columnVisibility,
  onToggleColumnVisibility,
  onClearAllColumnVisibility,
  onSelectAllColumnVisibility,
  onCustomFilterApply,
  containerclassName,
  layout = "default",
  belowSearchContent,
}: FiltersPanelProps) {
  const { isStaffMode } = useStaffMode();
  const hasConfigurablePanelFilters = filtersData.some(
    (f) => !isFilterHiddenFromUi(f),
  );
  const visibleFilters = filtersData.filter((f) => !isFilterHiddenFromUi(f));

  const renderCustomFilter = (f: IFilterData) =>
    f.render?.({
      value: filterValues?.[f.name],
      apply: (nextUiState, clause) =>
        onCustomFilterApply?.(
          f.name,
          f.fieldName,
          nextUiState,
          clause,
        ),
    }) ?? null;

  const renderFilterControl = (f: IFilterData) => {
    if (f.type === "text") {
      return (
        <Input
          title={f.customTitle || `${f.name}:`}
          name={f.name}
          value={filterValues?.[f.name] ?? ""}
          onChange={(
            e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
          ) =>
            onHandleFilters(
              f.name,
              e.target.value,
              f.fieldName,
              f.comparison,
            )
          }
          error={false}
        />
      );
    }
    if (f.type === "date") {
      return (
        <CalendarInput
          title={f.customTitle || `${f.name}:`}
          error={false}
          type={"range"}
          value={filterValues?.[f.name] ?? ""}
          onChange={(e: any) =>
            onHandleFilters(f.name, e, f.fieldName, f.comparison)
          }
        />
      );
    }
    if (f.type === "custom") {
      return renderCustomFilter(f);
    }
    if (f.type === "hidden") {
      return null;
    }
    if (f.type === "radio") {
      return (
        <FilterOptionsGroup
          title={f.customTitle || f.name}
          options={f.objectData || []}
          value={filterValues?.[f.name] ?? []}
          onChange={(value: DropDownOptionBase[]) =>
            onHandleFilters(
              f.name,
              value,
              f.fieldName,
              f.comparison,
              f.getPresetRange,
            )
          }
          allOptionLabel={f.allOptionLabel}
          disabled={f.disabled}
        />
      );
    }
    if (f.type === "select") {
      return (
        <DropDownMultiple<DropDownOptionBase>
          title={f.customTitle || `${f.name}:`}
          options={f.objectData || []}
          value={
            Array.isArray(filterValues?.[f.name])
              ? (filterValues[f.name][0] ?? null)
              : (filterValues?.[f.name] ?? null)
          }
          onChange={(value: DropDownOptionBase | null) =>
            onHandleFilters(
              f.name,
              value ? [value] : [],
              f.fieldName,
              f.comparison,
              f.getPresetRange,
            )
          }
          disabled={f.disabled}
          searchable={!f.disabledWrite}
          sortByName={f.sortByName}
        />
      );
    }
    return (
      <DropDownMultiple<DropDownOptionBase>
        title={f.customTitle || `${f.name}:`}
        options={f.objectData || []}
        value={filterValues?.[f.name] ?? []}
        onChange={(value: DropDownOptionBase[]) =>
          onHandleFilters(
            f.name,
            value,
            f.fieldName,
            f.comparison,
            f.getPresetRange,
          )
        }
        multiple
        disabled={f.disabled}
        searchable={!f.disabledWrite}
        sortByName={f.sortByName}
      />
    );
  };

  const filterCellClass = (f: IFilterData) => {
    if (!inlineToolbar) {
      return getFilterPanelColClass(f);
    }
    if (f.type === "radio") {
      return "w-full";
    }
    if (f.panelColSpan === "wide" || f.panelColSpan === "full") {
      return "w-full shrink-0 md:w-auto";
    }
    return "w-full shrink-0 md:w-64";
  };

  if (layout === "firmLogs") {
    return (
      <div
        className={`mb-5 flex w-full min-w-0 shrink-0 flex-col self-stretch bg-white pt-4 ${containerclassName ?? ""}`}
      >
        <div className="flex w-full flex-wrap items-center gap-3">
          {showSearchInput && (
            <div className="min-w-[12rem] flex-1">
              <InputText
                type="search"
                value={filterValues?.inputSearch ?? ""}
                onChange={(value) => onSearchChange?.(value)}
                placeholder={searchPlaceholder}
              />
            </div>
          )}
          <div className="ml-auto flex flex-wrap items-center gap-4">
            {visibleFilters.map((f) =>
              f.type === "custom" ? (
                <div key={f.name} className="shrink-0">
                  {renderCustomFilter(f)}
                </div>
              ) : null,
            )}
            {showExcelExport && (
              <LegacyButton
                text="Export"
                color="accent"
                style="outlined"
                content="textIcon"
                size="filter"
                onClick={onOpenExcelModal}
                icon={
                  <div className="flex h-5 w-5 items-center justify-center">
                    <Excel />
                  </div>
                }
                className="shrink-0 [&_span]:!normal-case"
              />
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white p-6 gap-4 flex flex-col w-full self-stretch min-w-0 ${containerclassName ?? ""}`}>
      {bannerContent && (
        <div className="animate-banner-content-enter">{bannerContent}</div>
      )}

      <div
        className={
          inlineToolbar
            ? "flex flex-wrap items-end gap-4"
            : "grid grid-cols-12 gap-4"
        }
      >
        {chunkPanelFilters(filtersData).map((chunk) =>
          chunk.group ? (
            <div
              key={`group-${chunk.group}`}
              className={
                inlineToolbar
                  ? "w-full"
                  : "col-span-12 rounded-md border border-gray-200 p-4"
              }
            >
              <p className="mb-3 text-base font-semibold text-gray-font">
                {chunk.group}
              </p>
              <div
                className={
                  inlineToolbar
                    ? "flex flex-wrap items-end gap-4"
                    : "grid grid-cols-12 gap-4"
                }
              >
                {chunk.items.map((f) => (
                  <div key={f.name} className={filterCellClass(f)}>
                    {renderFilterControl(f)}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            chunk.items.map((f) => (
              <div key={f.name} className={filterCellClass(f)}>
                {renderFilterControl(f)}
              </div>
            ))
          ),
        )}
        <div
          className={
            inlineToolbar
              ? "flex w-full min-w-0 basis-full flex-col gap-4"
              : "col-span-12 flex flex-col gap-4"
          }
        >
          {showSelectedFilters && hasConfigurablePanelFilters && (
            <div className="flex w-full min-w-0 flex-wrap items-center gap-x-4 gap-y-2 rounded-md border border-gray-100 p-3 text-sm">
              <div className="flex shrink-0 items-center gap-3">
                <span className="font-semibold text-gray-font">
                  Selected Filters
                </span>
                {hasSelectedFilters && (
                  <>
                    <span className="h-4 w-px bg-gray-300" />
                    <button
                      type="button"
                      onClick={onClearAllSelectedFilters}
                      className={`font-medium ${isStaffMode ? "text-secondary hover:text-secondary-dark" : "text-primary hover:text-primary-dark"} underline `}
                    >
                      Clear All Filters
                    </button>
                  </>
                )}
              </div>

              {!hasSelectedFilters ? (
                <p className="text-gray-500">No selected filters.</p>
              ) : (
                selectedFilterGroups.map((group) => (
                  <div
                    key={group.key}
                    className="flex shrink-0 flex-nowrap items-center gap-2"
                  >
                    <span className="font-semibold text-gray-font">
                      {selectedFilterGroupLabel(group.name)}
                    </span>
                    {group.values.map(
                      (value: DropDownOptionBase, index: number) => (
                        <span
                          key={`${group.key}-${value.id ?? value.name ?? index}`}
                          className="inline-flex items-center gap-1 rounded bg-gray-100 px-2 py-1 text-gray-font"
                        >
                          {value.name ?? String(value.id)}
                          <button
                            type="button"
                            onClick={() =>
                              onRemoveSelectedValue(group.key, value)
                            }
                            className="text-gray-500 hover:text-gray-700"
                          >
                            <Close className="w-4 h-4" />
                          </button>
                        </span>
                      ),
                    )}
                  </div>
                ))
              )}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3">
            {showSearchInput && (
              <div
                className={
                  inlineToolbar
                    ? "w-full md:min-w-[18rem] md:max-w-[600px] md:flex-1"
                    : "w-full lg:max-w-[600px]"
                }
              >
                <InputText
                  type="search"
                  value={filterValues?.inputSearch ?? ""}
                  onChange={(value) => onSearchChange?.(value)}
                  placeholder={searchPlaceholder}
                />
              </div>
            )}
            <div className="w-full md:w-auto flex-1 md:flex-auto">
              {searchToolbarAccessory}
            </div>

            <div className="flex w-full flex-shrink-0 flex-nowrap items-center gap-2 md:w-auto">
              {showExcelExport && (
                <div className="min-w-0 flex-1 basis-1/2 md:basis-auto md:flex-initial">
                  <Button
                    text="Export"
                    color={isStaffMode ? "secondary" : "primary"}
                    variant="ghost"
                    size="fixed"
                    onClick={onOpenExcelModal}
                    icon={<Excel />}
                  />
                </div>
              )}
              {showColumnVisibility && (
                <div className="min-w-0 flex-1 basis-1/2 md:basis-auto md:flex-initial">
                  <ColumnVisibilityMenu
                    headers={columnsDef}
                    columnVisibility={columnVisibility}
                    onToggle={onToggleColumnVisibility}
                    onClearAll={onClearAllColumnVisibility}
                    onSelectAll={onSelectAllColumnVisibility}
                  />
                </div>
              )}
            </div>
          </div>

          {belowSearchContent && (
            <div className="mt-4">{belowSearchContent}</div>
          )}
        </div>
      </div>
    </div>
  );
}
